import { useState, useRef, useCallback, useEffect } from 'react';

// ─────────────────────────────────────────────────────────────────
// Debounced, race-safe async typeahead for large masters (1000s of
// rows — items 3000+, customers 5000+). `fetcher(query)` resolves to
// an options array already shaped for AntD AutoComplete.
//
//   • DEBOUNCE   — one request after the user pauses, not one per
//                  keystroke, so a 3000-row ILIKE isn't hammered.
//   • RACE GUARD — each request carries a sequence token; a slower
//                  earlier response can never overwrite a newer one.
//   • MIN CHARS  — queries shorter than `minChars` are skipped (no
//                  wasteful empty scans / accidental full-table peeks).
//
// Returns { options, setOptions, run, cancel, searching }.
//   run(query)  — schedule a debounced search.
//   cancel()    — drop any pending request + invalidate in-flight
//                 responses (used by client-side branches so a stale
//                 server response can't clobber local results).
//   setOptions  — set results directly (client-side filtering).
// ─────────────────────────────────────────────────────────────────
export function useTypeaheadSearch(fetcher, { delay = 250, minChars = 1 } = {}) {
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const seqRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const cancel = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    seqRef.current += 1;          // invalidate any in-flight response
    setSearching(false);
  }, []);

  const run = useCallback((rawQuery) => {
    const query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (query.length < minChars) { seqRef.current += 1; setSearching(false); setOptions([]); return; }
    setSearching(true);
    timerRef.current = setTimeout(async () => {
      const token = ++seqRef.current;
      let opts = [];
      try { opts = await fetcher(query); } catch (_e) { opts = []; }
      if (token !== seqRef.current) return;       // superseded by a newer query
      setOptions(Array.isArray(opts) ? opts : []);
      setSearching(false);
    }, delay);
  }, [fetcher, delay, minChars]);

  return { options, setOptions, run, cancel, searching };
}
